import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PlanWaitlistService } from '@services/requests/housien-services/PlanWaitlistService';
import type { PlanWithFrequency } from '../../hooks/use-plans';

/**
 * Inline waitlist join for sold-out plans — backed by the real API.
 *
 * @param plans - Visible plans for the current segment.
 * @returns Waitlisted keys set and join handler.
 */
export function usePlanWaitlistFlow(plans: PlanWithFrequency[]) {
  const { t } = useTranslation('@pages/onboarding');
  const [waitlistedKeys, setWaitlistedKeys] = useState<Set<string>>(new Set());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    PlanWaitlistService.list()
      .then((res) => {
        if (cancelled) return;
        const keys = new Set(res.items.map((i) => i.plan_key));
        setWaitlistedKeys(keys);
      })
      .catch(() => { /* silent — local state stays empty */ })
      .finally(() => { if (!cancelled) setHydrated(true); });
    return () => { cancelled = true; };
  }, []);

  const joinSoldOut = useCallback(
    (plan: PlanWithFrequency) => {
      // Optimistic UI: mark waitlisted immediately, roll back on failure.
      setWaitlistedKeys((prev) => new Set(prev).add(plan.key));
      PlanWaitlistService.join({ plan_key: plan.key })
        .then(() => {
          // Success toast fires ONLY when the request actually resolves, so
          // success and error are mutually exclusive — exactly one toast per
          // outcome (previously the success toast fired unconditionally, so a
          // rejected request showed BOTH a success and an error toast).
          toast.success(t('planChoice.waitlist.joined'));
        })
        .catch(() => {
          setWaitlistedKeys((prev) => {
            const next = new Set(prev);
            next.delete(plan.key);
            return next;
          });
          toast.error(t('planChoice.waitlist.joinFailed'));
        });
    },
    [t],
  );

  const isWaitlisted = useCallback(
    (planKey: string) => waitlistedKeys.has(planKey),
    [waitlistedKeys],
  );

  return { joinSoldOut, isWaitlisted, hydrated };
}
